/**
 * Utility functions for rubric workflow question processing
 */

/**
 * Gets the workflow for a specific code type from the rubric version
 *
 * @param rubricVersion - Full rubric version with all workflows
 * @param codeType - Type of code ('procedure', 'diagnosis', 'modifier')
 * @returns The code workflow or undefined if not found
 */
export const getWorkflowForCodeType = (
  rubricVersion: CQA.FullRubricVersion | null | undefined,
  codeType: string
): CQA.CodeWorkflow | undefined => {
  if (!rubricVersion?.workflows) {
    return undefined;
  }

  // Map code type to workflow type
  const workflowTypeMap: Record<string, CQA.CodeWorkflowType> = {
    'procedure': 'PROCEDURE',
    'diagnosis': 'DIAGNOSIS',
    'modifier': 'MODIFIER',
    'case': 'CASE',
    'span': 'SPAN'
  };

  const workflowType = workflowTypeMap[codeType.toLowerCase()];
  if (!workflowType) {
    return undefined;
  }

  const workflowEntry = rubricVersion.workflows.find(
    w => w.workflow.codeType === workflowType
  );

  if (!workflowEntry) {
    return undefined;
  }

  // Return workflow object with questions attached
  // (questions are a sibling to workflow in the API response structure)
  return {
    ...workflowEntry.workflow,
    questions: workflowEntry.questions
  };
};

/**
 * Finds an attribute for a specific question from the code's attributes
 *
 * @param attributes - Array of attributes
 * @param questionId - The question ID to find
 * @returns The matching attribute or undefined
 */
export const findAnswerForQuestion = (
  attributes: CQA.Attribute[] | undefined,
  questionId: number
): CQA.Attribute | undefined => {
  if (!attributes) {
    return undefined;
  }
  return attributes.find(a => a.questionId === questionId);
};

/**
 * Maps a code type string to the corresponding attribute entity type for API calls.
 * This is a centralized mapping used across all rubric question components.
 *
 * @param codeType - The code type ('procedure', 'diagnosis', 'modifier', 'case')
 * @returns The corresponding attribute entity type
 */
/**
 * Gets question IDs that should be cleared when a question's answer changes.
 * Uses the backend-provided visible questions to determine downstream questions
 * and also finds stale attributes from previously visited workflow branches.
 *
 * @param visibleQuestions - Currently visible workflow questions (from backend evaluation)
 * @param attributes - Current saved attributes on the entity
 * @param changedQuestionId - The question whose answer is changing
 * @returns Array of question IDs whose attributes should be deleted
 */
export const getQuestionIdsToClear = (
  visibleQuestions: CQA.WorkflowQuestion[],
  attributes: CQA.Attribute[],
  changedQuestionId: number
): number[] => {
  // Find downstream: all visible questions after the changed question
  const changedIndex = visibleQuestions.findIndex(
    wq => wq.question.id === changedQuestionId
  );
  const downstream = changedIndex >= 0
    ? visibleQuestions.slice(changedIndex + 1).map(wq => wq.question.id)
    : [];

  // Find stale: attributes for questions not currently visible (from old branches)
  const visibleIds = new Set(visibleQuestions.map(wq => wq.question.id));
  visibleIds.add(changedQuestionId);
  const staleIds = attributes
    .filter(a => !visibleIds.has(a.questionId))
    .map(a => a.questionId);

  // Combine and deduplicate
  return [...new Set([...downstream, ...staleIds])];
};

export const codeTypeToEntityType = (codeType: string): CQA.AttributeEntityType => {
  switch (codeType.toLowerCase()) {
    case 'procedure': return 'code_procedure';
    case 'diagnosis': return 'code_diagnosis';
    case 'modifier': return 'code_modifier';
    case 'case': return 'case';
    case 'span': return 'span';
    default: return 'code_procedure'; // fallback
  }
};
